import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Calendar, Users, Award, Building2, Building, ArrowLeft, ThumbsUp, ThumbsDown } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useGame } from '../hooks/useGame';
import { sanitizeDescription } from '../utils/sanitize';

export function GameDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { game, isLoading, error } = useGame(parseInt(id || '0', 10));
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);

  const formatNumber = (num: number | undefined | null): string => {
    if (num === undefined || num === null) return '0';
    return num.toLocaleString();
  };

  const formatMonth = (dateStr: string) => {
    const [year, month] = dateStr.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1);
    return new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' }).format(date);
  };

  const getRatingColor = (percentage: number): string => {
    if (percentage >= 95) return 'text-emerald-600';
    if (percentage >= 85) return 'text-blue-600';
    if (percentage >= 70) return 'text-indigo-600';
    if (percentage >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getRatingText = (percentage: number): string => {
    if (percentage >= 95) return 'Overwhelmingly Positive';
    if (percentage >= 85) return 'Very Positive';
    if (percentage >= 70) return 'Mostly Positive';
    if (percentage >= 50) return 'Mixed';
    return 'Mostly Negative';
  };

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
          <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
        </div>
      </div>
    );
  }

  if (error || !game) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="text-center">
          <h2 className="text-xl font-bold text-red-600 mb-2">Error</h2>
          <p>{error || 'Failed to load game details'}</p>
          <button
            onClick={() => navigate('/')}
            className="mt-4 px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  const sanitizedDescription = sanitizeDescription(game.description || '');
  const shortDescription = sanitizedDescription.slice(0, 250);
  const hasLongDescription = sanitizedDescription.length > 250;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <button
        onClick={() => navigate('/')}
        className="flex items-center text-gray-600 hover:text-gray-900 mb-6"
      >
        <ArrowLeft className="w-5 h-5 mr-2" />
        Back to Games
      </button>

      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        <div className="relative h-64 overflow-hidden">
          <img
            src={game.imageUrl}
            alt={game.name}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
          <h1 className="absolute bottom-6 left-6 text-4xl font-bold text-white">
            {game.name}
          </h1>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h2 className="text-xl font-semibold mb-4">About</h2>
              <p className="text-gray-600 mb-4">
                {isDescriptionExpanded ? sanitizedDescription : shortDescription}
                {hasLongDescription && (
                  <button
                    onClick={() => setIsDescriptionExpanded(!isDescriptionExpanded)}
                    className="ml-2 text-indigo-600 hover:text-indigo-700 font-medium"
                  >
                    {isDescriptionExpanded ? 'Show Less' : 'Read More'}
                  </button>
                )}
              </p>

              <div className="flex flex-wrap gap-2 mb-6">
                {game.categories?.map((category) => (
                  <span
                    key={category}
                    className="px-3 py-1 rounded-full bg-indigo-100 text-indigo-800 text-sm"
                  >
                    {category}
                  </span>
                ))}
              </div>

              <div className="space-y-3">
                <div className="flex items-center text-gray-600">
                  <Calendar className="w-5 h-5 mr-2" />
                  <span>Released: {game.releaseDate || 'Unknown'}</span>
                </div>
                <div className="flex items-center text-gray-600">
                  <Building2 className="w-5 h-5 mr-2" />
                  <span>Developer: {game.developer || 'Unknown'}</span>
                </div>
                <div className="flex items-center text-gray-600">
                  <Building className="w-5 h-5 mr-2" />
                  <span>Publisher: {game.publisher || 'Unknown'}</span>
                </div>
              </div>
            </div>

            <div>
              <h2 className="text-xl font-semibold mb-4">Statistics</h2>
              <div className="bg-gray-50 rounded-lg p-6 space-y-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center text-gray-600">
                    <Users className="w-5 h-5 mr-2" />
                    <span>Current Players</span>
                  </div>
                  <span className="font-semibold text-lg">
                    {formatNumber(game.playerCount)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center text-gray-600">
                    <Award className="w-5 h-5 mr-2" />
                    <span>All-Time Peak</span>
                  </div>
                  <span className="font-semibold text-lg">
                    {formatNumber(game.peakAllTime)}
                  </span>
                </div>

                {game.rating && (
                  <div className="pt-4 border-t border-gray-200">
                    <h3 className="text-lg font-medium mb-3">User Reviews</h3>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className={`font-medium ${getRatingColor(game.rating.percentage)}`}>
                          {getRatingText(game.rating.percentage)}
                        </span>
                        <span className="font-medium">
                          {game.rating.percentage}%
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-gray-600">
                        <div className="flex items-center">
                          <ThumbsUp className="w-4 h-4 mr-1" />
                          <span>Positive</span>
                        </div>
                        <span>{formatNumber(game.rating.positive)}</span>
                      </div>
                      <div className="flex items-center justify-between text-gray-600">
                        <div className="flex items-center">
                          <ThumbsDown className="w-4 h-4 mr-1" />
                          <span>Negative</span>
                        </div>
                        <span>{formatNumber(game.rating.negative)}</span>
                      </div>
                      <div className="flex items-center justify-between text-gray-600">
                        <span>Total Reviews</span>
                        <span>{formatNumber(game.rating.total)}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {game.monthlyStats && game.monthlyStats.length > 0 && (
            <div className="mt-8">
              <h2 className="text-xl font-semibold mb-4">Monthly Statistics</h2>
              <div className="space-y-6">
                <div className="bg-white rounded-lg shadow border p-4">
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart
                        data={game.monthlyStats.map(stat => ({
                          ...stat,
                          month: formatMonth(stat.month)
                        }))}
                        margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" />
                        <YAxis />
                        <Tooltip />
                        <Line
                          type="monotone"
                          dataKey="averagePlayers"
                          stroke="#4f46e5"
                          name="Average Players"
                        />
                        <Line
                          type="monotone"
                          dataKey="peakPlayers"
                          stroke="#059669"
                          name="Peak Players"
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Month
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Avg Players
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Peak Players
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Gain
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            % Change
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {game.monthlyStats.map((stat) => (
                          <tr key={stat.month}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {formatMonth(stat.month)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {formatNumber(stat.averagePlayers)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {formatNumber(stat.peakPlayers)}
                            </td>
                            <td className={`px-6 py-4 whitespace-nowrap text-sm ${
                              stat.gain > 0 ? 'text-green-600' : 'text-red-600'
                            }`}>
                              {stat.gain > 0 ? '+' : ''}{formatNumber(stat.gain)}
                            </td>
                            <td className={`px-6 py-4 whitespace-nowrap text-sm ${
                              stat.gainPercentage > 0 ? 'text-green-600' : 'text-red-600'
                            }`}>
                              {stat.gainPercentage > 0 ? '+' : ''}{stat.gainPercentage.toFixed(1)}%
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}